import { Map as ImmutableMap } from 'immutable';
import { Fragment, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import count from 'transmute/count';
import { NavMarker } from 'react-rhumb';
import FileMetadataRecord from 'conversations-internal-schema/file-metadata/records/FileMetadataRecord';
import { getVisitorLastReadAtTimestamp, getFileAttachments, getOffsetTimestamp, getOffsetOrdinal, getHasMore, getFriendlyNameResults
// @ts-expect-error module not typed
} from 'conversations-message-history/thread-history/operators/getters';
import MessageHistoryCore from 'conversations-message-history/web/message-history/components/MessageHistoryCore';
import { View } from 'foundations-components/components/View';
// @ts-expect-error module not typed
import { useSenders } from 'conversations-thread-data/senders/hooks/useSenders';
// @ts-expect-error module not typed
import { useLegacyRecipients } from 'conversations-thread-data/legacy-recipients/hooks/useLegacyRecipients';
import { useFileAttachments } from 'composer-data/attachments/public/hooks';
import { getFileIdsFromHistory } from 'conversations-thread-data/thread-history/public/operators';
import { getData } from 'conversations-async-data/async-data/operators/getters';
import { isSucceeded } from 'conversations-async-data/async-data/operators/statusComparators';
// @ts-expect-error module not typed
import { getSenderLocatorsFromHistory } from 'conversations-thread-data/senders/operators/getSenderLocatorsFromHistory';
// @ts-expect-error module not typed
import { getLatestAgentMessage } from 'conversations-message-history/thread-history/operators/getLatestAgentMessage';
// @ts-expect-error module not typed
import { getStatusTimestamp } from 'conversations-message-history/common-message-format/operators/commonMessageFormatGetters';
import { getPresentVisitorLocators } from 'conversations-thread-data/presence/public/selectors';
import { getRecipientsFromHistory, getRecipientLocatorsFromHistory
// @ts-expect-error module not typed
} from '../operators/getRecipientsFromHistory';
import { useVisitorsBatch } from 'conversations-thread-data/visitor/hooks/useVisitorsBatch';
import { gatherPerformanceMetrics } from '../../performance-metrics/operators/gatherPerformanceMetrics';
import { SEND_MESSAGE } from '../../performance-metrics/constants/performanceConstants';
import { getIsUngatedForNextBestActions, getIsUngatedForCopilotPreviews, getIsUngatedForChatspot, getIsUngatedForAutogenerateCopilotCallSummary, getIsUngatedForOmnichannelCallSummaryAiSkill } from 'conversations-thread-data/auth/public/selectors';
import { toJSTyped } from 'conversations-thread-data/utils/immutable/toJSTyped';
import { withRouter } from 'react-router-dom';
// @ts-expect-error module not typed
import ThreadHistoryScrollWrapper from './ThreadHistoryScrollWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const AgentThreadHistory = ({
  allowContactPromotions = false,
  currentUserId,
  fetchHistory,
  historyConfig,
  messages,
  portalId,
  threadHistory,
  threadId,
  vid,
  slots,
  getAdditionalPositionDescriptors,
  status,
  ungatedForThreadMessagesUIImprovements
}) => {
  //Check to see if there is an active send message performance mark.
  //If so, measure the difference between that mark's start time
  //and the most recent agent message's sent timestamp
  const sendMessageMarks = performance.getEntriesByName(`${SEND_MESSAGE}-start`);
  const sentTimestamp = getStatusTimestamp(getLatestAgentMessage(threadHistory));
  if (sendMessageMarks.length && sendMessageMarks[0].startTime < sentTimestamp) {
    performance.mark(`${SEND_MESSAGE}-stop`, {
      startTime: sentTimestamp
    });
    gatherPerformanceMetrics(SEND_MESSAGE);
  }
  const presentSenderLocators = useSelector(state => getPresentVisitorLocators(state, {
    threadId
  }));
  const senderLocators = useMemo(() => getSenderLocatorsFromHistory(threadHistory), [threadHistory]);
  const recipientLocators = useMemo(() => getRecipientLocatorsFromHistory(threadHistory), [threadHistory]);
  const recipientVids = useMemo(() => getRecipientsFromHistory(threadHistory), [threadHistory]);
  const {
    data: visitorRecipients = {}
  } = useVisitorsBatch({
    vids: toJSTyped(recipientVids)
  });
  const messagesCount = count(messages ? messages.toArray() : []);
  const fetchOlderHistory = useCallback(() => {
    if (getHasMore(threadHistory)) {
      fetchHistory({
        threadId,
        offsetTimestamp: getOffsetTimestamp(threadHistory),
        offsetOrdinal: getOffsetOrdinal(threadHistory)
      });
    }
  }, [fetchHistory, threadHistory, threadId]);
  const {
    senders
  } = useSenders({
    senderLocators,
    vid
  });
  const {
    recipients
  } = useLegacyRecipients({
    recipientLocators
  });
  const fileAttachments = getFileAttachments(threadHistory) || ImmutableMap(); // need Map fallback for deepmerge
  const fileIdsFromHistory = useMemo(() => getFileIdsFromHistory(threadHistory), [threadHistory]);
  const {
    fileAttachments: asyncFileAttachments
  } = useFileAttachments({
    fileAttachmentIds: fileIdsFromHistory
  });
  const loadedFileAttachments = useMemo(() => fileAttachments.mergeDeep(asyncFileAttachments.filter(isSucceeded).map(getData).filter(Boolean)), [fileAttachments, asyncFileAttachments]);
  const castFileAttachments = useMemo(() => loadedFileAttachments.map(attachment => attachment === null || attachment === void 0 ? void 0 : attachment.update('fileMetadata', metadata => metadata && new FileMetadataRecord(toJSTyped(metadata)))), [loadedFileAttachments]);
  const ungatedForNextBestActions = useSelector(getIsUngatedForNextBestActions);
  const ungatedForCopilotPreview = useSelector(getIsUngatedForCopilotPreviews);
  const ungatedForChatspot = useSelector(getIsUngatedForChatspot);
  const ungatedForAutogenerateCopilotCallSummary = useSelector(getIsUngatedForAutogenerateCopilotCallSummary);
  const ungatedForOmnichannelCallSummaryAiSkill = useSelector(getIsUngatedForOmnichannelCallSummaryAiSkill);
  const friendlyNames = getFriendlyNameResults(threadHistory);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: "CHAT_FEED_SUCCESS"
    }), /*#__PURE__*/_jsx(ThreadHistoryScrollWrapper, {
      currentUserId: currentUserId,
      fetchOlderHistory: fetchOlderHistory,
      messages: messages,
      persistedMessageCount: messagesCount,
      status: status,
      children: /*#__PURE__*/_jsx(View, {
        paddingInline: "0",
        zIndex: "0",
        paddingBlockStart: "100",
        flexShrink: 0,
        children: /*#__PURE__*/_jsx(MessageHistoryCore, {
          allowContactPromotions: allowContactPromotions,
          fileAttachments: castFileAttachments,
          friendlyNames: friendlyNames,
          historyConfig: historyConfig,
          indexedAsyncFileAttachments: asyncFileAttachments,
          messages: messages,
          portalId: portalId,
          senders: senders,
          presentSenderLocators: presentSenderLocators,
          visitorLastReadTimestamp: getVisitorLastReadAtTimestamp(threadHistory),
          getAdditionalPositionDescriptors: getAdditionalPositionDescriptors,
          vid: vid,
          visitorRecipients: visitorRecipients,
          ungatedForNextBestActions: ungatedForNextBestActions,
          ungatedForNewForkedComponent: true,
          ungatedForCopilotPreview: ungatedForCopilotPreview,
          ungatedForChatspot: ungatedForChatspot,
          ungatedForAutogenerateCopilotCallSummary: ungatedForAutogenerateCopilotCallSummary,
          ungatedForOmnichannelCallSummaryAiSkill: ungatedForOmnichannelCallSummaryAiSkill,
          ungatedForThreadMessagesUIImprovements: ungatedForThreadMessagesUIImprovements,
          threadHistory: threadHistory,
          threadId: threadId,
          legacyRecipients: recipients,
          slots: slots
        })
      })
    }, `${threadId}`)]
  });
};
AgentThreadHistory.displayName = 'AgentThreadHistory';
export default withRouter(AgentThreadHistory);